/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { createTheme } from '@mui/material';
import type {} from '@mui/x-date-pickers/themeAugmentation';

import { CIColors, CIFonts } from 'shared';

import { BreadcrumbsComponent } from './components/BreadCrumbs';
import { ButtonComponent } from './components/Button';
import { ChipComponent } from './components/Chip';
import { IconButtonComponent } from './components/IconButton';
import { InputLabelComponent } from './components/InputLabel';
import { OutlinedInputComponent } from './components/OutlinedInput';
import { SliderComponent } from './components/Slider';
import { SwitchComponent } from './components/Switch';
import { TooltipComponent } from './components/Tooltip';
import { CheckboxProps } from './props/Checkbox/Checkbox.props';

declare module '@mui/material/styles/createPalette' {
  interface Palette {
    scalefieldTextPrimary: Palette['primary'];
    scalefieldTextSecondary: Palette['primary'];
    scalefieldCardBackground: Palette['primary'];
    chipStatusOpen: Palette['primary'];
    chipStatusPending: Palette['primary'];
    chipStatusCancelled: Palette['primary'];
    chipStatusDone: Palette['primary'];
    graphicElements: Palette['primary'];
    tertiary: Palette['primary'];
  }
  interface PaletteOptions {
    scalefieldTextPrimary: PaletteOptions['primary'];
    scalefieldTextSecondary: PaletteOptions['primary'];
    scalefieldCardBackground: PaletteOptions['primary'];
    chipStatusOpen: PaletteOptions['primary'];
    chipStatusPending: PaletteOptions['primary'];
    chipStatusCancelled: PaletteOptions['primary'];
    chipStatusDone: PaletteOptions['primary'];
    graphicElements: PaletteOptions['primary'];
    tertiary: PaletteOptions['primary'];
  }
}

export const useCreateTheme = () =>
  createTheme({
    components: {
      MuiBreadcrumbs: BreadcrumbsComponent,
      MuiButton: ButtonComponent,
      MuiCheckbox: {
        defaultProps: CheckboxProps(),
        styleOverrides: {
          root: {
            '&:hover': {
              backgroundColor: 'transparent',
            },
          },
        },
      },
      MuiChip: ChipComponent,
      MuiIconButton: IconButtonComponent,
      MuiInputLabel: InputLabelComponent,
      MuiOutlinedInput: OutlinedInputComponent,
      MuiSlider: SliderComponent,
      MuiSwitch: SwitchComponent,
      MuiTooltip: TooltipComponent,
      MuiButtonBase: {
        defaultProps: {
          disableRipple: DISABLE_BUTTON_RIPPLE,
        },
      },
      MuiPickersToolbar: {
        styleOverrides: {
          root: {
            color: CIColors.Tertiary,
            backgroundColor: `${CIColors.Tertiary}20`,
          },
        },
      },
      // MuiPickerDTTabs: {
      //   tabs: {
      //     backgroundColor: `${CIColors.Tertiary}20`,
      //     color: CIColors.Tertiary,
      //   },
      // },
      MuiPickersToolbarText: {
        styleOverrides: {
          root: {
            color: CIColors.Tertiary,
            '&.Mui-selected': {
              color: CIColors.Tertiary,
              opacity: '0.7',
            },
          },
        },
      },
      MuiClockNumber: {
        styleOverrides: {
          root: {
            color: CIColors.Text,
            '& .MuiTypography-root': {
              color: 'inherit',
            },
            '&.Mui-disabled': {
              color: 'rgba(25, 56, 105, 0.2)',
              '& .MuiTypography-root': {
                color: 'inherit',
              },
            },
          },
        },
      },
      MuiPickersDay: {
        styleOverrides: {
          root: {
            '&:focus.MuiPickersDay-daySelected': {
              backgroundColor: `${CIColors.Tertiary} !important`,
            },
            '&:hover.MuiPickersDay-daySelected': {
              backgroundColor: CIColors.Tertiary,
            },
          },
        },
      },
    },
    typography: {
      fontFamily: CIFonts.Text,
      fontSize: 13,
      h1: {
        fontSize: '36px',
        lineHeight: '36px',
        letterSpacing: '.75px',
        fontWeight: 700,
        color: CIColors.Primary,
      },
      h2: {
        fontSize: '27px',
        lineHeight: '27px',
        letterSpacing: '.75px',
        fontWeight: 700,
        color: CIColors.Primary,
      },
      h3: {
        fontSize: '21px',
        lineHeight: '21px',
        fontWeight: 700,
        color: CIColors.Tertiary,
      },
      h4: {
        color: CIColors.Tertiary,
        fontSize: '14px',
        lineHeight: '14px',
        fontWeight: 700,
      },
      body1: {
        fontSize: '16px',
        lineHeight: 1.6,
        color: CIColors.Text,
      },
      subtitle1: {
        color: '#40424a',
      },
      subtitle2: {
        color: '#40424a',
      },
      caption: {
        color: '#40424a',
      },
    },
    palette: {
      primary: {
        main: CIColors.Primary,
        contrastText: '#FFFFFF',
      },
      secondary: {
        main: CIColors.Secondary,
        contrastText: '#FFFFFF',
      },
      text: {
        primary: CIColors.Text,
      },
      background: {
        default: '#ffffff',
      },
      scalefieldTextPrimary: {
        main: CIColors.Primary,
      },
      scalefieldTextSecondary: {
        main: CIColors.Secondary,
      },
      scalefieldCardBackground: {
        main: '#F6F7F8',
      },
      chipStatusOpen: {
        main: '#0052CC',
      },
      chipStatusPending: {
        main: '#FFAB00',
      },
      chipStatusCancelled: {
        main: '#FF5630',
      },
      chipStatusDone: {
        main: '#36B37E',
      },
      success: {
        main: CIColors.Success,
      },
      error: {
        main: CIColors.Error,
      },
      warning: {
        main: CIColors.Warning,
      },
      graphicElements: {
        main: CIColors.GraphicElements,
      },
      tertiary: {
        main: CIColors.Tertiary,
        contrastText: '#FFFFFF',
      },
    },
  });
